'use strict';
/* ==========================================================================
   levels/map.js — the hub: the student council's DOCKET, a parchment sheet pinned to the kraft desk (DESIGN.md §1–§2).
   Left: Judge Okafor + a speech bubble. Right: the docket, one ruled entry per lesson.json step (so a new step shows up
   by itself): a wax-seal number in the step's colour, the title in woodtype-ish slab type, the tag in hand lettering.
   A finished step gets a SIGNED stamp (its score when Game.progress.scores[id] = {best, of}); the first step not done
   yet gets a red ink "next!" arrow. → Game.replace(new Levels[id]()). On a phone held upright the steps are full-width
   rows in the portrait dock (Dock.map) and the docket is only the picture (no hit areas, no bubble).
   ========================================================================== */
(function () {
  const CV = () => window.Civic;
  const ROW_W = 1000;
  // the step colours down the docket: the three branch colours + the stamp red
  const COLORS = ['#9e2b25', '#1f2f55', '#b08a2e', '#2e5a4f'];
  // the Judge's line on the map (captions only)
  const SAY = {
    hello: 'Order in the classroom. We take the docket in order: item one first.',
    next: 'Motion carried. Next item on the docket.',
    done: 'All four signed. This council is adjourned.',
  };

  /* the entries, laid out from lesson.json steps (numbers count the steps that earn a stamp, like the home page) */
  function tags() {
    const S = (Game.lesson && Game.lesson.steps) || [], n = Math.max(1, S.length);
    const top = 300, span = 640, gap = span / n, h = Math.min(140, gap - 24);   // rows end above y 940: the title's ▶ Play (960, 972) never lands on one
    let k = 0;
    return S.map((s, i) => {
      const stamps = s.stamp !== false; if (stamps) k++;
      return { id: s.id, n: stamps ? k : null, title: s.title, sub: s.tag || '', h, x: 1235 + [0, 8, -6, 6, -4][i % 5],
        y: top + (i + 0.5) * gap, rot: [-0.008, 0.006, -0.005, 0.007, -0.004][i % 5], color: COLORS[(k - 1 + COLORS.length) % COLORS.length] };
    });
  }
  const nextStep = () => tags().find(tg => tg.n != null && !Game.progress.stamps[tg.id]);

  function drawRow(tg, i, hover, t0) {
    const Cv = CV(), s = pop(T, t0 + 0.35 + i * 0.12, 0.4, 2.2);
    if (s <= 0) return;
    const H = tg.h;
    g.save();
    g.translate(tg.x, tg.y + (hover ? -6 : 0)); g.rotate(tg.rot); g.scale(s * (hover ? 1.02 : 1), s * (hover ? 1.02 : 1));
    paperRect(0, 0, ROW_W, Math.round(H), hover ? '#fffaf0' : PAL.cream, { seed: 40 + i, depth: hover ? 1.6 : 0.9 });
    paperRect(-ROW_W / 2 + 10, 0, 16, Math.round(H - 12), tg.color, { seed: 45 + i, depth: 0 });
    Cv.seal(-ROW_W / 2 + 104, 0, H * 0.34, { text: tg.n == null ? '' : String(tg.n), seed: 50 + i * 3, ribbon: false });
    g.font = `${Math.round(H * 0.34)}px 'Alfa Slab One'`; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillStyle = Cv.C.ink;
    g.fillText(String(tg.title).toUpperCase(), -ROW_W / 2 + 186, -H * 0.16);
    hand(tg.sub, -ROW_W / 2 + 190, H * 0.25, Math.min(Math.round(H * 0.27), UI.text(36)), t0 + 0.8 + i * 0.12, { align: 'left', color: Cv.C.navy, seed: 58 + i });
    // the clerk's dotted leader to the signature box
    ink([[ROW_W / 2 - 250, H * 0.3], [ROW_W / 2 - 40, H * 0.3]], { w: 2, color: 'rgba(43,36,32,0.35)', seed: 70 + i, passes: 1, step: 40 });
    g.restore();
  }

  class MapScene extends Scene {
    enter() {
      const st = Game.progress.stamps, T_ = tags().filter(tg => tg.n != null);
      this.allDone = T_.every(tg => st[tg.id]);
      this.say = this.allDone ? SAY.done : T_.some(tg => st[tg.id]) ? SAY.next : SAY.hello;
      this.stampNew = Game._justStamped; Game._justStamped = null;
      Audio.play('paper', { gain: 0.5 });
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
    }
    open(id) {
      Audio.play('pop', { gain: 0.6 });
      const C = Levels[id];
      if (typeof C === 'function') Game.replace(new C());
      else UI.toast('coming soon');
    }
    keyDown(k) {
      const all = tags(), n = parseInt(k, 10);
      if (n >= 1 && n <= all.length) this.open(all[n - 1].id);
      if (k === 'Enter') this.open((nextStep() || all[0]).id);
    }

    draw() {
      UI.allLessons();                       // "← All lessons": back to the site's home page (core/ui.js)
      const t0 = this.enteredAt, t = this.t, docked = UI.docked(), Cv = CV();
      g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
      Cv.bunting(-20, 1940, 8, { sag: 34, n: 19, seed: 5, t0: t0 + 0.1 });

      // the docket sheet
      Cv.parchment(1235, 590, 1180, 900, { seed: 83, rot: 0.004, depth: 1.8 });
      tape(700, 158, 170, -0.5, undefined, 5); tape(1770, 158, 170, 0.5, undefined, 6);
      Cv.woodtype('THE DOCKET', 1235, 196, 64, { color: Cv.C.navy, ink: Cv.C.cream, t0: t0 + 0.2, seed: 304 });
      hand('Coral Ridge Middle Student Council · Session No. 1', 1235, 264, 38, t0 + 0.5, { color: Cv.C.brick, seed: 305 });
      Cv.seal(1740, 980, 50, { text: 'CR', seed: 91, s: pop(T, t0 + 0.9, 0.3, 2.4), rot: -0.2 });

      // the Judge + speech (the speech goes into the dock when docked)
      const rise = E.outBack(P(T, t0 + 0.2, t0 + 0.7), 1.4);
      Puppet.draw('judge', 330, 1080 + (1 - rise) * 500, 700, { facing: 1, t: T, env: t > 0.6 && t < 3 ? 0.25 + 0.25 * Math.sin(T * 17) : 0 });
      if (docked) Dock.map({ say: this.say, who: (LessonHooks.speakers && LessonHooks.speakers.judge || {}).name, open: id => this.open(id) });
      else if (t > 0.6) UI.speech(this.say, 400, 250, { side: 'left', w: 480, t0: t0 + 0.6 });

      const nx = nextStep();
      tags().forEach((tg, i) => {
        const hit = docked ? { hover: false, clicked: false } : UI._hit('tag' + i, tg.x, tg.y, ROW_W, UI.tap(tg.h));
        if (hit.hover && !this['hov' + i]) Audio.play('paper', { gain: 0.2, rate: 1.3 });
        this['hov' + i] = hit.hover;
        drawRow(tg, i, hit.hover, t0);
        if (Game.progress.stamps[tg.id]) {
          let s = 1;
          if (this.stampNew === tg.id) {
            const at = t0 + 0.9;
            s = pop(T, at, 0.18, 1.2) * (1 + 1.4 * (1 - P(T, at, at + 0.12)));
            if (T >= at && !this.stampPlayed) { this.stampPlayed = true; Audio.play('stamp'); }
          } else s = pop(T, t0 + 0.8, 0.25, 1.5);
          const sc = Game.progress.scores && Game.progress.scores[tg.id];
          const label = (Game.lesson.completeCard && Game.lesson.completeCard.title) || 'SIGNED';
          UI.stamp(sc && sc.best != null && sc.of ? `${sc.best}/${sc.of}` : label, tg.x + ROW_W / 2 - 150, tg.y + 4, { rot: -0.14, s: s * 0.5, color: Cv.C.brick });
        }
        if (!this.allDone && !docked && nx && nx.id === tg.id) {   // docked: the dock row says next!
          const ns = pop(T, t0 + 1.4, 0.4), bob = Math.sin(T * 4) * 8;
          if (ns > 0) {
            g.save(); g.globalAlpha = ns;
            const ax = tg.x - ROW_W / 2 - 20 - bob;
            inkArrow(ax - 90, tg.y - 40, ax, tg.y, { w: 6, color: Cv.C.brick, seed: 600 + i, bulge: 20 });
            hand('next!', ax - 150, tg.y - 58, 42, 0, { dur: 0.01, color: Cv.C.brick, rot: -0.1, seed: 610 + i });
            g.restore();
          }
        }
        if (hit.clicked) this.open(tg.id);
      });
    }
  }
  MapScene.tags = () => tags().map(tg => ({ id: tg.id, x: tg.x, y: tg.y }));   // tools/integration.js clicks these

  Levels.map = MapScene;
})();
